import QuickFilterSelect from './QuickFilterSelect';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { Box } from 'foundations-components/transitional/components/Box';
import UISelect from 'UIComponents/input/UISelect';
import { NumberInput } from 'foundations-components/transitional/components/NumberInput';
import { mapOperatorNameToOperatorType, mapOperatorTypeToOperator, numberDurationOperatorOptions, NUMBER_DURATION_DEFAULT_OPERATOR, NUMBER_DURATION_TWO_VALUE_OPERATORS } from '../utils/numberDurationQuickFilterUtils';
import once from 'transmute/once';
import I18n from 'I18n';
import invariant from 'react-utils/invariant';
import isNumber from 'transmute/isNumber';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const getDurationOptions = once(() => [{
  text: I18n.text('customerDataFiltersUI.quickFiltersBar.duration.time.seconds'),
  value: 'seconds'
}, {
  text: I18n.text('customerDataFiltersUI.quickFiltersBar.duration.time.minutes'),
  value: 'minutes'
}, {
  text: I18n.text('customerDataFiltersUI.quickFiltersBar.duration.time.hours'),
  value: 'hours'
}, {
  text: I18n.text('customerDataFiltersUI.quickFiltersBar.duration.time.days'),
  value: 'days'
}]);
const getDefaultState = once(() => ({
  durationValue: getDurationOptions()[0].value,
  isActive: false,
  leftInputEnabled: NUMBER_DURATION_TWO_VALUE_OPERATORS.includes(NUMBER_DURATION_DEFAULT_OPERATOR),
  leftValue: 0,
  mainValue: 0,
  operatorType: NUMBER_DURATION_DEFAULT_OPERATOR
}));

/**
 * Determines the base duration unit (seconds/minutes/hours/days) given a set of times in milliseconds
 * and converts those times to be in that unit
 */
export const fromMilliseconds = values => {
  let timeValues = values.map(milliseconds => milliseconds / 1000);
  let durationValue = 'seconds';
  if (timeValues.every(seconds => seconds % 60 === 0)) {
    timeValues = timeValues.map(seconds => seconds / 60);
    durationValue = 'minutes';
    if (timeValues.every(minutes => minutes % 60 === 0)) {
      timeValues = timeValues.map(minutes => minutes / 60);
      durationValue = 'hours';
      if (timeValues.every(hours => hours % 24 === 0)) {
        timeValues = timeValues.map(hours => hours / 24);
        durationValue = 'days';
      }
    }
  }
  return {
    durationValue,
    timeValues
  };
};
const getStateFromCondition = condition => {
  if (condition) {
    const value = 'value' in condition ? condition.value : undefined;
    const highValue = 'highValue' in condition ? condition.highValue : undefined;
    const operatorName = 'name' in condition ? condition.name : undefined;
    const operatorType = mapOperatorNameToOperatorType(operatorName);
    if (NUMBER_DURATION_TWO_VALUE_OPERATORS.includes(operatorType)) {
      if (isNumber(value) && isNumber(highValue)) {
        const {
          durationValue,
          timeValues
        } = fromMilliseconds([value, highValue]);
        return {
          durationValue,
          isActive: true,
          leftInputEnabled: true,
          leftValue: timeValues[0],
          mainValue: timeValues[1],
          operatorType
        };
      }
    } else if (isNumber(value)) {
      const {
        durationValue,
        timeValues
      } = fromMilliseconds([value]);
      return {
        durationValue,
        isActive: true,
        leftInputEnabled: false,
        leftValue: getDefaultState().leftValue,
        mainValue: timeValues[0],
        operatorType
      };
    }
  }
  return getDefaultState();
};
const DurationQuickFilter = ({
  buttonProperties,
  condition,
  disableTruncation,
  isFirst,
  property,
  onValueChange
}) => {
  const initialState = getStateFromCondition(condition);
  const [durationValue, setDurationValue] = useState(initialState.durationValue);
  const [isActive, setIsActive] = useState(initialState.isActive);
  const [isOpen, setIsOpen] = useState(false);
  const [leftInputEnabled, setLeftInputEnabled] = useState(initialState.leftInputEnabled);
  const [leftValue, setLeftValue] = useState(initialState.leftValue);
  const [mainValue, setMainValue] = useState(initialState.mainValue);
  const [operatorType, setOperatorType] = useState(initialState.operatorType);
  const resetToDefaultState = useCallback(() => {
    setLeftInputEnabled(getDefaultState().leftInputEnabled);
    setLeftValue(getDefaultState().leftValue);
    setMainValue(getDefaultState().mainValue);
    setOperatorType(getDefaultState().operatorType);
    setIsActive(getDefaultState().isActive);
  }, []);
  useEffect(() => {
    if (!condition) {
      resetToDefaultState();
    }
  }, [condition, resetToDefaultState]);
  const onApplyChanges = useCallback(() => {
    const Operator = mapOperatorTypeToOperator(operatorType);
    invariant(Operator, `Unable to find a operator for operator type "${operatorType}"`);
    const leftValueConverted = Number(I18n.moment.duration(leftValue, durationValue).valueOf());
    const mainValueConverted = Number(I18n.moment.duration(mainValue, durationValue).valueOf());
    if (leftInputEnabled) {
      if (mainValue < leftValue) {
        const smallValue = mainValue;
        setMainValue(leftValue);
        setLeftValue(smallValue);
      }
      const highValue = Math.max(leftValueConverted, mainValueConverted);
      const lowValue = Math.min(leftValueConverted, mainValueConverted);
      onValueChange(Operator.of(property).set('value', lowValue).set('highValue', highValue));
    } else {
      onValueChange(Operator.of(property).set('value', mainValueConverted));
    }
    setIsOpen(false);
    setIsActive(true);
  }, [durationValue, leftInputEnabled, leftValue, mainValue, onValueChange, operatorType, property]);
  const onClearChanges = useCallback(() => {
    if (condition) {
      onValueChange();
      resetToDefaultState();
    }
  }, [condition, onValueChange, resetToDefaultState]);
  const onToggleOpen = () => {
    setIsOpen(currentIsOpen => !currentIsOpen);
  };
  const onOperatorChange = evt => {
    const newOperator = evt.target.value;
    setOperatorType(newOperator);
    if (NUMBER_DURATION_TWO_VALUE_OPERATORS.includes(newOperator)) {
      setLeftInputEnabled(true);
    } else {
      setLeftInputEnabled(false);
      setLeftValue(getDefaultState().leftValue);
    }
  };
  const onLeftValueChange = evt => {
    setLeftValue(evt.target.value);
  };
  const onMainValueChange = evt => {
    setMainValue(evt.target.value);
  };
  const onDurationValueChange = evt => {
    setDurationValue(evt.target.value);
  };
  const operatorOptions = useMemo(() => numberDurationOperatorOptions.map(({
    langToken,
    symbol,
    value
  }) => ({
    text: I18n.text(langToken),
    dropdownText: `${symbol} ${I18n.text(langToken)}`,
    value
  })), []);
  return /*#__PURE__*/_jsx(QuickFilterSelect, {
    property: property,
    buttonProperties: buttonProperties,
    buttonText: property.displayName,
    condition: condition,
    disableTruncation: disableTruncation,
    isActive: isActive,
    isFirst: isFirst,
    isOpen: isOpen,
    popoverContent: {
      body: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(TFlex, {
          paddingBlockEnd: "200",
          children: /*#__PURE__*/_jsx(Box, {
            children: /*#__PURE__*/_jsx(UISelect, {
              "data-selenium-test": "DurationQuickFilter-comparator",
              menuWidth: 'auto',
              onChange: onOperatorChange,
              options: operatorOptions,
              value: operatorType
            })
          })
        }), /*#__PURE__*/_jsxs(TFlex, {
          align: "center",
          children: [leftInputEnabled && /*#__PURE__*/_jsx(Box, {
            grow: 1,
            paddingInlineEnd: "100",
            children: /*#__PURE__*/_jsx(NumberInput, {
              "data-selenium-test": "DurationQuickFilter-leftValue",
              disabled: !leftInputEnabled
              // @ts-expect-error UINumberInput hasn't been properly migrated yet
              ,
              onChange: onLeftValueChange,
              value: leftValue
            })
          }), leftInputEnabled && /*#__PURE__*/_jsx(Box, {
            grow: 1,
            paddingInlineEnd: "100",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "customerDataFiltersUI.quickFiltersBar.duration.conjunction.and"
            })
          }), /*#__PURE__*/_jsx(Box, {
            grow: 1,
            paddingInlineEnd: "300",
            children: /*#__PURE__*/_jsx(NumberInput, {
              "data-selenium-test": "DurationQuickFilter-rightValue"
              // @ts-expect-error UINumberInput hasn't been properly migrated yet
              ,
              onChange: onMainValueChange,
              value: mainValue
            })
          }), /*#__PURE__*/_jsx(Box, {
            grow: 2,
            children: /*#__PURE__*/_jsx(UISelect, {
              "data-selenium-test": "DurationQuickFilter-unit",
              onChange: onDurationValueChange,
              options: getDurationOptions(),
              value: durationValue
            })
          })]
        })]
      }),
      footer: /*#__PURE__*/_jsxs(TFlex, {
        align: "center",
        direction: "row",
        justify: "between",
        wrap: "nowrap",
        children: [/*#__PURE__*/_jsx(Button, {
          "data-selenium-test": "DurationQuickFilter-apply",
          size: "small",
          use: "tertiary",
          onClick: onApplyChanges,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataFiltersUI.quickFiltersBar.number.actions.apply"
          })
        }), /*#__PURE__*/_jsx(Button, {
          "data-selenium-test": "DurationQuickFilter-clear",
          size: "small",
          use: "tertiary-light",
          onClick: onClearChanges,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataFiltersUI.quickFiltersBar.number.actions.clear"
          })
        })]
      })
    },
    onToggleOpen: onToggleOpen,
    onValueChange: onValueChange,
    width: "350px"
  });
};
export default DurationQuickFilter;